/*
 * camera-reader/framing.js — eyes-free camera framing guidance + auto-capture.
 *
 * The #1 blind-user problem in camera OCR is not accuracy — it's aiming the
 * camera (studies: 7/12 blind users couldn't take one usable photo without
 * guidance, and a simple "hold steady / got it" beat verbose directions). So
 * this module turns the live preview into an audio proximity signal:
 *
 *   - a lightweight per-frame score (NO OpenCV, NO OCR): edge/ink density in the
 *     centre guide region, gated by brightness. Text pages score high; blank
 *     walls and BLURRY frames score low (blur collapses edge energy → doubles as
 *     a focus check). Runs on a 160px downscale every ~180ms.
 *   - a WebAudio proximity tone whose pitch+volume rise with the score, going
 *     steady when the page is well framed. Muteable.
 *   - an auto-capture state machine: fire when score is high AND stable across N
 *     frames AND enough time since the last shot. Manual shutter always works.
 *   - torch + zoom via MediaStreamTrack constraints, feature-detected (Android
 *     Chromium only for torch; iOS Safari exposes neither → buttons stay hidden).
 *
 * AudioContext is resumed inside the camera-start gesture. Reuses
 * OcrCamera.autoCropDocument (pure canvas fn) for a rough margin crop.
 *
 * Public: window.CRFraming = { start, stop, switchCamera, captureNow,
 *                              setAutoCapture, setMuted, isOpen }
 */
(function () {
  'use strict';

  var GREEN = 0.42;          // guide turns green above this score
  var CAP = 0.48;            // auto-capture score threshold
  var STABLE_FRAMES = 5;     // ~0.9s of stable framing before auto-shot
  var STABLE_TOL = 0.14;     // max score spread across the window
  var MIN_GAP_MS = 1800;     // min time between captures
  var SAMPLE_MS = 180;

  var cfg = {};
  var els = {};
  var stream = null, track = null, facing = 'environment';
  var loop = null, scores = [], lastCapture = 0, open = false;
  var autoCapture = true, muted = false;
  var onCapture = null;
  var lastAnnounce = 0, lastAnnounceKey = '';

  // WebAudio proximity tone
  var actx = null, osc = null, gain = null, toneOn = false;

  var tmp = null, tctx = null;   // downscale sampler

  function t(k, d) { return (cfg.i18n && cfg.i18n[k]) || d || k; }
  function announce(msg, key) {
    var now = Date.now();
    if (key && key === lastAnnounceKey && now - lastAnnounce < 2500) return; // throttle repeats
    lastAnnounce = now; lastAnnounceKey = key || '';
    try { if (window.__announce) window.__announce(msg); } catch (e) {}
  }

  // ── AudioContext / tone ──
  function ensureAudio() {
    if (actx) { if (actx.state === 'suspended') actx.resume(); return; }
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      actx = new AC();
      osc = actx.createOscillator();
      gain = actx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 320;
      gain.gain.value = 0;
      osc.connect(gain); gain.connect(actx.destination);
      osc.start();
    } catch (e) { actx = null; }
  }
  function updateTone(score) {
    if (!actx || muted) { if (gain) try { gain.gain.setTargetAtTime(0, actx.currentTime, 0.05); } catch (e) {} return; }
    try {
      var f = 300 + score * 520;
      var g = score < 0.12 ? 0 : 0.02 + score * 0.06;
      osc.frequency.setTargetAtTime(f, actx.currentTime, 0.06);
      gain.gain.setTargetAtTime(g, actx.currentTime, 0.06);
      toneOn = g > 0;
    } catch (e) {}
  }
  function silenceTone() { if (gain && actx) try { gain.gain.setTargetAtTime(0, actx.currentTime, 0.03); } catch (e) {} }
  function chime() {
    if (!actx || muted) return;
    try {
      var o = actx.createOscillator(), g = actx.createGain();
      o.type = 'sine'; o.connect(g); g.connect(actx.destination);
      var n = actx.currentTime;
      o.frequency.setValueAtTime(660, n); o.frequency.setValueAtTime(990, n + 0.08);
      g.gain.setValueAtTime(0.0001, n); g.gain.exponentialRampToValueAtTime(0.12, n + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, n + 0.22);
      o.start(n); o.stop(n + 0.24);
    } catch (e) {}
  }

  // ── frame score: edge/ink density in centre, gated by brightness ──
  function scoreFrame() {
    var v = els.video;
    if (!v || !v.videoWidth) return 0;
    if (!tmp) { tmp = document.createElement('canvas'); tctx = tmp.getContext('2d', { willReadFrequently: true }); }
    var W = 160, H = 120;
    tmp.width = W; tmp.height = H;
    if (!window.__drawCameraFrame(tctx, v, 0, 0, W, H)) return 0;   // no frame this tick
    var d;
    try { d = tctx.getImageData(0, 0, W, H).data; } catch (e) { return 0; }
    // luminance plane
    var lum = new Float32Array(W * H);
    var sum = 0;
    for (var i = 0, p = 0; i < d.length; i += 4, p++) {
      var l = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
      lum[p] = l; sum += l;
    }
    var mean = sum / (W * H);
    // horizontal+vertical gradient energy in the centre region (guide box ~ 60%)
    var x0 = (W * 0.14) | 0, x1 = (W * 0.86) | 0, y0 = (H * 0.18) | 0, y1 = (H * 0.82) | 0;
    var edge = 0, cnt = 0, strong = 0;
    for (var y = y0; y < y1; y++) {
      for (var x = x0; x < x1; x++) {
        var idx = y * W + x;
        var gx = Math.abs(lum[idx] - lum[idx + 1]);
        var gy = Math.abs(lum[idx] - lum[idx + W]);
        var g = gx + gy;
        edge += g; cnt++;
        if (g > 40) strong++;   // count of strong transitions (text strokes)
      }
    }
    var edgeAvg = cnt ? edge / cnt : 0;         // ~0..60
    var strongFrac = cnt ? strong / cnt : 0;    // fraction of text-edge pixels
    // brightness gate: too dark (unlit) or blown-out reduce confidence
    var brightGate = mean < 45 ? mean / 45 : (mean > 250 ? 0.6 : 1);
    // combine: strongFrac is the strongest text signal; edgeAvg helps
    var raw = strongFrac * 3.2 + (edgeAvg / 60) * 0.6;
    var score = Math.max(0, Math.min(1, raw * brightGate));
    return score;
  }

  function tick() {
    if (!open) return;
    var s = scoreFrame();
    updateTone(s);
    // guide colour
    if (els.guide) els.guide.classList.toggle('detected', s >= GREEN);

    // spoken hints (throttled, simple — not verbose directions)
    if (s < 0.12) announce(t('aim_dark', 'Point the camera at text.'), 'aim');
    else if (s >= GREEN) announce(t('aim_hold', 'Text detected. Hold steady.'), 'hold');

    // stability window
    scores.push(s);
    if (scores.length > STABLE_FRAMES) scores.shift();
    if (autoCapture && scores.length >= STABLE_FRAMES && Date.now() - lastCapture > MIN_GAP_MS) {
      var min = Math.min.apply(null, scores), max = Math.max.apply(null, scores);
      if (min >= CAP && (max - min) <= STABLE_TOL) {
        doCapture(true);
      }
    }
  }

  function doCapture(isAuto) {
    if (!els.video || !els.video.videoWidth) return;
    lastCapture = Date.now();
    scores = [];
    var canvas = document.createElement('canvas');
    canvas.width = els.video.videoWidth;
    canvas.height = els.video.videoHeight;
    // No frame this instant (an interrupted camera): a press is told so out loud —
    // a blind reader cannot see that nothing happened; the auto shot just waits.
    if (!window.__drawCameraFrame(canvas.getContext('2d'), els.video, 0, 0)) {
      if (!isAuto) announce(window.__cameraNoFrame(), 'cap');
      return;
    }
    var cropped = canvas;
    try { if (window.OcrCamera && window.OcrCamera.autoCropDocument) cropped = window.OcrCamera.autoCropDocument(canvas); } catch (e) {}
    silenceTone();
    chime();
    announce(t('captured', 'Captured. Reading…'), 'cap');
    if (onCapture) onCapture(cropped, { auto: !!isAuto });
  }

  // ── torch / zoom capability wiring ──
  function wireCapabilities() {
    var caps = null;
    try { caps = track && track.getCapabilities ? track.getCapabilities() : null; } catch (e) {}
    // torch
    if (els.torch) {
      if (caps && 'torch' in caps) {
        els.torch.style.display = '';
        els.torch.onclick = function () {
          var on = els.torch.getAttribute('aria-pressed') === 'true';
          try {
            track.applyConstraints({ advanced: [{ torch: !on }] });
            els.torch.setAttribute('aria-pressed', (!on).toString());
          } catch (e) {}
        };
      } else els.torch.style.display = 'none';
    }
    // zoom
    if (els.zoom) {
      if (caps && caps.zoom) {
        els.zoom.style.display = '';
        els.zoom.min = caps.zoom.min; els.zoom.max = caps.zoom.max;
        els.zoom.step = caps.zoom.step || 0.1;
        els.zoom.value = (track.getSettings && track.getSettings().zoom) || caps.zoom.min;
        els.zoom.oninput = function () { try { track.applyConstraints({ advanced: [{ zoom: parseFloat(els.zoom.value) }] }); } catch (e) {} };
        if (els.zoomWrap) els.zoomWrap.style.display = '';
      } else {
        // fallback: CSS crop-zoom on the video element (iOS/desktop with no zoom cap)
        if (els.zoom) {
          els.zoom.style.display = '';
          els.zoom.min = 1; els.zoom.max = 4; els.zoom.step = 0.1; els.zoom.value = 1;
          els.zoom.oninput = function () { if (els.video) els.video.style.transform = 'scale(' + els.zoom.value + ')'; };
          if (els.zoomWrap) els.zoomWrap.style.display = '';
        }
      }
    }
  }

  // ── lifecycle ──
  // The shared camera door. It owns the watchdog for a getUserMedia that never
  // settles, the constraint-relaxation ladder, and — the part this tool needed
  // — CLASSIFYING the refusal and carrying a phrase for each cause in every
  // locale. A bare getUserMedia has one outcome for "you said no", "another app
  // holds it" and "this machine has no camera", and the single message we used
  // to print told all three to go and allow camera access. Comment #1689 was
  // written by someone on a desktop with no camera at all.
  var camMgr = null;
  async function startStream() {
    if (stream) stopStream();
    var want = { facingMode: facing, width: 1920, height: 1440 };
    if (window.CameraManager) {
      if (!camMgr) camMgr = new window.CameraManager({ slug: 'camera-reader' });
      stream = await camMgr.start(want);
    } else {
      // Fail-open: the primitive is loaded with a tag, and a page that somehow
      // lacks it should still open a camera the old way rather than not at all.
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: want.width }, height: { ideal: want.height } },
        audio: false
      });
    }
    // Frames, not play(): on a camera that never sends one, play() never
    // settles and the reader sat on "opening the camera" for good. After 6 s
    // the camera door says why (and keeps waiting — a slow camera still comes
    // up); a camera that never does is thrown as a refusal the orchestrator
    // can name ('unresponsive') — the words a hung start gets too.
    var ok;
    if (camMgr) ok = await camMgr.show(els.video, stream);
    else if (window.__cameraFrames) ok = await window.__cameraFrames(els.video, { stream: stream });
    else {
      els.video.srcObject = stream; els.video.muted = true; els.video.setAttribute('playsinline', '');
      try { var p = els.video.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
      ok = true;
    }
    if (!ok) {
      stopStream();
      var none = new Error('the camera sends no frames');
      none.code = 'unresponsive';
      throw none;
    }
    track = stream.getVideoTracks()[0] || null;
    wireCapabilities();
  }
  function stopStream() {
    if (camMgr) { try { camMgr.stop(); } catch (e) {} }
    if (stream) { stream.getTracks().forEach(function (t) { try { t.stop(); } catch (e) {} }); stream = null; track = null; }
    if (els.video) { try { els.video.pause(); els.video.srcObject = null; els.video.style.transform = ''; } catch (e) {} }
  }

  async function start(config) {
    cfg = config || {};
    els = cfg.els || {};
    onCapture = cfg.onCapture || null;
    if (typeof cfg.autoCapture === 'boolean') autoCapture = cfg.autoCapture;
    ensureAudio();                       // resume inside the start gesture
    await startStream();
    open = true;
    scores = [];
    if (loop) clearInterval(loop);
    loop = setInterval(tick, SAMPLE_MS);
    announce(t('cam_ready', 'Camera ready. Point at text.'), 'ready');
  }

  function stop() {
    open = false;
    if (loop) { clearInterval(loop); loop = null; }
    silenceTone();
    stopStream();
    if (els.guide) els.guide.classList.remove('detected');
  }

  async function switchCamera() {
    facing = facing === 'environment' ? 'user' : 'environment';
    try { await startStream(); } catch (e) { if (window.__toast) window.__toast((e && e.message) || 'camera error', 'error'); }
  }

  function captureNow() { doCapture(false); }
  function setAutoCapture(v) { autoCapture = !!v; }
  function setMuted(v) { muted = !!v; if (muted) silenceTone(); }
  function isOpen() { return open; }

  // Why a refusal was refused, and the sentence to show for it. Kept in the
  // camera module so the orchestrator never has to know the shapes an error can
  // arrive in — a wrapper can reject with a plain STRING, in which case
  // `err.name` is undefined and a naive reader prints "undefined: undefined".
  function describeError(err) {
    var code = (err && err.code)
      || (window.CameraManager ? window.CameraManager.classify(err) : null)
      || 'generic';
    var msg = null;
    if (window.CameraManager) {
      try {
        if (!camMgr) camMgr = new window.CameraManager({ slug: 'camera-reader' });
        msg = camMgr.getErrorMessage(code);
      } catch (e) {}
    }
    return { code: code, message: msg };
  }

  window.CRFraming = {
    start: start, stop: stop, switchCamera: switchCamera, captureNow: captureNow,
    setAutoCapture: setAutoCapture, setMuted: setMuted, isOpen: isOpen,
    describeError: describeError
  };
})();
